/**
 * Validation module for the Covered Calls Tracker.
 *
 * All validators return the complete set of errors (not fail-fast),
 * identifying every invalid field in a single pass.
 */

import {
  BuyWriteInput,
  StockPurchaseInput,
  OptionSaleInput,
  TradeUpdateInput,
  ExitInput,
  RollInput,
  ResaleInput,
  BrokerProfileInput,
  ProtectivePutEntryInput,
  AddProtectivePutInput,
  PutOutcomeInput,
  OptionOutcomeInput,
  DividendInput,
  DividendUpdateInput,
} from '../types/inputs';
import { RollType, Exchange, InstrumentClass, PutOutcome, OptionOutcome } from '../types/enums';
import { OPTION_CONTRACT_MULTIPLIER } from '../calculations/commission';

/** A single validation error identifying a field and the reason it is invalid. */
export interface ValidationError {
  field: string;
  message: string;
}

// ─── Range Constants ────────────────────────────────────────────────────────

const PRICE_MIN = 0.01;
const PRICE_MAX = 999_999.99;

const QUANTITY_MIN = 1;
const QUANTITY_MAX = 100_000;

const PREMIUM_MIN = 0.00;
const PREMIUM_MAX = 999_999.99;

const COMMISSION_MIN = 0.00;
const COMMISSION_MAX = 99_999.99;

const THRESHOLD_MIN = 1;
const THRESHOLD_MAX = 50;

// Dividend per-share rate: strictly positive, capped, and cent-precise.
const PER_SHARE_MIN = 0.01;
const PER_SHARE_MAX = 999_999.99;

// ─── Helper Validators ──────────────────────────────────────────────────────

function validatePrice(value: number, field: string, errors: ValidationError[]): void {
  if (typeof value !== 'number' || isNaN(value) || value < PRICE_MIN || value > PRICE_MAX) {
    errors.push({ field, message: `${field} must be between ${PRICE_MIN} and ${PRICE_MAX}` });
  }
}

function validateQuantity(value: number, field: string, errors: ValidationError[]): void {
  if (typeof value !== 'number' || isNaN(value) || !Number.isInteger(value) || value < QUANTITY_MIN || value > QUANTITY_MAX) {
    errors.push({ field, message: `${field} must be an integer between ${QUANTITY_MIN} and ${QUANTITY_MAX}` });
  }
}

function validatePremium(value: number, field: string, errors: ValidationError[]): void {
  if (typeof value !== 'number' || isNaN(value) || value < PREMIUM_MIN || value > PREMIUM_MAX) {
    errors.push({ field, message: `${field} must be between ${PREMIUM_MIN} and ${PREMIUM_MAX}` });
  }
}

function validateCommission(value: number, field: string, errors: ValidationError[]): void {
  if (typeof value !== 'number' || isNaN(value) || value < COMMISSION_MIN || value > COMMISSION_MAX) {
    errors.push({ field, message: `${field} must be between ${COMMISSION_MIN} and ${COMMISSION_MAX}` });
  }
}

function validateDateAfter(
  dateStr: string,
  referenceStr: string,
  dateField: string,
  referenceField: string,
  errors: ValidationError[],
): void {
  const date = new Date(dateStr);
  const reference = new Date(referenceStr);
  if (isNaN(date.getTime()) || isNaN(reference.getTime()) || date <= reference) {
    errors.push({ field: dateField, message: `${dateField} must be after ${referenceField}` });
  }
}

function validateRequired(value: unknown, field: string, errors: ValidationError[]): boolean {
  if (value === undefined || value === null || value === '') {
    errors.push({ field, message: `${field} is required` });
    return false;
  }
  return true;
}

function validateISODate(value: string, field: string, errors: ValidationError[]): boolean {
  if (!value || isNaN(new Date(value).getTime())) {
    errors.push({ field, message: `${field} must be a valid date` });
    return false;
  }
  return true;
}

function validateTicker(value: string, field: string, errors: ValidationError[]): void {
  if (!value || typeof value !== 'string' || value.trim().length === 0) {
    errors.push({ field, message: `${field} is required` });
  }
}

/**
 * Validates a dividend per-share cash rate: must be a number, not NaN,
 * strictly greater than 0, at most 999,999.99, and no more than 2 decimal
 * places. Distinct from validatePremium (which allows 0) and validatePrice.
 * The 2dp check uses the same epsilon-tolerant rounding as roundTo2dp.
 */
function validatePerShare(value: number, field: string, errors: ValidationError[]): void {
  if (
    typeof value !== 'number' ||
    isNaN(value) ||
    value < PER_SHARE_MIN ||
    value > PER_SHARE_MAX ||
    Math.round((value + Number.EPSILON) * 100) / 100 !== value
  ) {
    errors.push({
      field,
      message: `${field} must be between ${PER_SHARE_MIN} and ${PER_SHARE_MAX} with at most 2 decimal places`,
    });
  }
}

// ─── Public Validators ──────────────────────────────────────────────────────

/**
 * Validates a buy/write trade input.
 * Returns all validation errors (not fail-fast).
 */
export function validateBuyWriteInput(input: BuyWriteInput): ValidationError[] {
  const errors: ValidationError[] = [];

  validateTicker(input.ticker, 'ticker', errors);
  if (validateRequired(input.tradeDate, 'tradeDate', errors)) {
    validateISODate(input.tradeDate, 'tradeDate', errors);
  }
  validatePrice(input.purchasePrice, 'purchasePrice', errors);
  validateQuantity(input.quantity, 'quantity', errors);
  validatePrice(input.strikePrice, 'strikePrice', errors);
  validatePremium(input.premium, 'premium', errors);
  // Validate per-leg commissions (new) or legacy single commission
  if (input.stockCommission !== undefined) {
    validateCommission(input.stockCommission, 'stockCommission', errors);
  }
  if (input.optionCommission !== undefined) {
    validateCommission(input.optionCommission, 'optionCommission', errors);
  }
  if (input.commission !== undefined) {
    validateCommission(input.commission, 'commission', errors);
  }
  if (input.stockCommission === undefined && input.optionCommission === undefined && input.commission === undefined) {
    errors.push({ field: 'stockCommission', message: 'Commission is required for at least one leg.' });
  }

  if (validateRequired(input.expiryDate, 'expiryDate', errors)) {
    if (validateISODate(input.expiryDate, 'expiryDate', errors)) {
      if (input.tradeDate && !isNaN(new Date(input.tradeDate).getTime())) {
        validateDateAfter(input.expiryDate, input.tradeDate, 'expiryDate', 'tradeDate', errors);
      }
    }
  }

  return errors;
}

/**
 * Validates a stock purchase input.
 * Returns all validation errors (not fail-fast).
 */
export function validateStockPurchaseInput(input: StockPurchaseInput): ValidationError[] {
  const errors: ValidationError[] = [];

  validateTicker(input.ticker, 'ticker', errors);
  if (validateRequired(input.purchaseDate, 'purchaseDate', errors)) {
    validateISODate(input.purchaseDate, 'purchaseDate', errors);
  }
  validatePrice(input.purchasePrice, 'purchasePrice', errors);
  validateQuantity(input.quantity, 'quantity', errors);
  validateCommission(input.commission, 'commission', errors);

  return errors;
}

/**
 * Validates an option sale input.
 * Returns all validation errors (not fail-fast).
 */
export function validateOptionSaleInput(input: OptionSaleInput): ValidationError[] {
  const errors: ValidationError[] = [];

  validatePrice(input.strikePrice, 'strikePrice', errors);
  validatePremium(input.premium, 'premium', errors);
  validateCommission(input.commission, 'commission', errors);

  if (validateRequired(input.expiryDate, 'expiryDate', errors)) {
    validateISODate(input.expiryDate, 'expiryDate', errors);
  }

  return errors;
}

/**
 * Validates a trade update input.
 * Only validates fields that are provided (partial update).
 * Returns all validation errors (not fail-fast).
 */
export function validateTradeUpdateInput(input: TradeUpdateInput): ValidationError[] {
  const errors: ValidationError[] = [];

  if (input.purchasePrice !== undefined) {
    validatePrice(input.purchasePrice, 'purchasePrice', errors);
  }
  if (input.quantity !== undefined) {
    validateQuantity(input.quantity, 'quantity', errors);
  }
  if (input.strikePrice !== undefined) {
    validatePrice(input.strikePrice, 'strikePrice', errors);
  }
  if (input.premium !== undefined) {
    validatePremium(input.premium, 'premium', errors);
  }
  if (input.commission !== undefined) {
    validateCommission(input.commission, 'commission', errors);
  }
  if (input.expiryDate !== undefined) {
    validateISODate(input.expiryDate, 'expiryDate', errors);
  }
  if (input.tradeDate !== undefined) {
    validateISODate(input.tradeDate, 'tradeDate', errors);
  }

  // Cross-field: if both expiryDate and tradeDate provided, expiry must be after trade date
  if (input.expiryDate && input.tradeDate) {
    const expiry = new Date(input.expiryDate);
    const trade = new Date(input.tradeDate);
    if (!isNaN(expiry.getTime()) && !isNaN(trade.getTime())) {
      validateDateAfter(input.expiryDate, input.tradeDate, 'expiryDate', 'tradeDate', errors);
    }
  }

  return errors;
}

/**
 * Validates an exit (close position) input.
 * Returns all validation errors (not fail-fast).
 */
export function validateExitInput(input: ExitInput): ValidationError[] {
  const errors: ValidationError[] = [];

  if (validateRequired(input.exitDate, 'exitDate', errors)) {
    validateISODate(input.exitDate, 'exitDate', errors);
  }
  validatePrice(input.exitStockPrice, 'exitStockPrice', errors);
  validatePremium(input.exitOptionBuybackCost, 'exitOptionBuybackCost', errors);
  validateCommission(input.stockSaleCommission, 'stockSaleCommission', errors);
  validateCommission(input.optionBuybackCommission, 'optionBuybackCommission', errors);

  return errors;
}

/**
 * Validates a roll option input.
 * Also validates roll-type-specific constraints against current strike/expiry.
 * Returns all validation errors (not fail-fast).
 */
export function validateRollInput(
  input: RollInput,
  currentStrike: number,
  currentExpiry: string,
): ValidationError[] {
  const errors: ValidationError[] = [];

  validatePremium(input.buybackCost, 'buybackCost', errors);
  validatePrice(input.newStrikePrice, 'newStrikePrice', errors);
  validatePremium(input.newPremium, 'newPremium', errors);
  validateCommission(input.buybackCommission, 'buybackCommission', errors);
  validateCommission(input.saleCommission, 'saleCommission', errors);

  if (validateRequired(input.newExpiryDate, 'newExpiryDate', errors)) {
    validateISODate(input.newExpiryDate, 'newExpiryDate', errors);
  }

  // Roll-type-specific constraint validation
  const newStrike = input.newStrikePrice;
  const newExpiry = new Date(input.newExpiryDate);
  const curExpiry = new Date(currentExpiry);

  switch (input.rollType) {
    case RollType.UP:
      if (typeof newStrike === 'number' && !isNaN(newStrike) && newStrike <= currentStrike) {
        errors.push({
          field: 'newStrikePrice',
          message: 'Rolling up requires new strike price to be higher than current strike',
        });
      }
      break;
    case RollType.DOWN:
      if (typeof newStrike === 'number' && !isNaN(newStrike) && newStrike >= currentStrike) {
        errors.push({
          field: 'newStrikePrice',
          message: 'Rolling down requires new strike price to be lower than current strike',
        });
      }
      break;
    case RollType.OUT:
      if (!isNaN(newExpiry.getTime()) && !isNaN(curExpiry.getTime()) && newExpiry <= curExpiry) {
        errors.push({
          field: 'newExpiryDate',
          message: 'Rolling out requires new expiry date to be later than current expiry',
        });
      }
      break;
    case RollType.UP_AND_OUT:
      if (typeof newStrike === 'number' && !isNaN(newStrike) && newStrike <= currentStrike) {
        errors.push({
          field: 'newStrikePrice',
          message: 'Rolling up and out requires new strike price to be higher than current strike',
        });
      }
      if (!isNaN(newExpiry.getTime()) && !isNaN(curExpiry.getTime()) && newExpiry <= curExpiry) {
        errors.push({
          field: 'newExpiryDate',
          message: 'Rolling up and out requires new expiry date to be later than current expiry',
        });
      }
      break;
  }

  return errors;
}

/**
 * Validates a resale (option re-sale on existing stock holding) input.
 * Returns all validation errors (not fail-fast).
 */
export function validateResaleInput(input: ResaleInput): ValidationError[] {
  const errors: ValidationError[] = [];

  validatePrice(input.strikePrice, 'strikePrice', errors);
  validatePremium(input.premium, 'premium', errors);
  validateCommission(input.commission, 'commission', errors);

  if (validateRequired(input.expiryDate, 'expiryDate', errors)) {
    validateISODate(input.expiryDate, 'expiryDate', errors);
  }

  return errors;
}

/**
 * Validates a broker commission profile input against the exchange-keyed
 * schedule model. Returns all validation errors (not fail-fast): every
 * invalid field across the profile and all of its schedules is reported in a
 * single pass.
 */
export function validateBrokerProfileInput(input: BrokerProfileInput): ValidationError[] {
  const errors: ValidationError[] = [];

  // brokerName is required (non-empty after trim).
  if (!input.brokerName || typeof input.brokerName !== 'string' || input.brokerName.trim().length === 0) {
    errors.push({ field: 'brokerName', message: 'brokerName is required' });
  }

  const schedules = input.schedules;

  // schedules must contain at least one entry.
  if (!Array.isArray(schedules) || schedules.length === 0) {
    errors.push({ field: 'schedules', message: 'At least one commission schedule is required' });
    return errors;
  }

  // No duplicate (exchange, instrumentClass) pairs across schedules.
  const seenPairs = new Set<string>();
  const reportedDuplicates = new Set<string>();
  for (const schedule of schedules) {
    const key = `${schedule.exchange}|${schedule.instrumentClass}`;
    if (seenPairs.has(key)) {
      if (!reportedDuplicates.has(key)) {
        errors.push({
          field: 'schedules',
          message: `Duplicate schedule for (${schedule.exchange}, ${schedule.instrumentClass})`,
        });
        reportedDuplicates.add(key);
      }
    } else {
      seenPairs.add(key);
    }
  }

  // Per-schedule validation.
  schedules.forEach((schedule, i) => {
    const prefix = `schedules[${i}]`;

    // exchange must be a member of Exchange.
    if (!Object.values(Exchange).includes(schedule.exchange)) {
      errors.push({ field: `${prefix}.exchange`, message: `${prefix}.exchange must be a valid exchange` });
    }

    // instrumentClass must be a member of InstrumentClass.
    if (!Object.values(InstrumentClass).includes(schedule.instrumentClass)) {
      errors.push({
        field: `${prefix}.instrumentClass`,
        message: `${prefix}.instrumentClass must be a valid instrument class`,
      });
    }

    // flatFee must be a number >= 0.
    if (typeof schedule.flatFee !== 'number' || isNaN(schedule.flatFee) || schedule.flatFee < 0) {
      errors.push({ field: `${prefix}.flatFee`, message: `${prefix}.flatFee must be a non-negative number` });
    }

    // percentageRate must be a number >= 0.
    if (
      typeof schedule.percentageRate !== 'number' ||
      isNaN(schedule.percentageRate) ||
      schedule.percentageRate < 0
    ) {
      errors.push({
        field: `${prefix}.percentageRate`,
        message: `${prefix}.percentageRate must be a non-negative number`,
      });
    }

    // perUnitFee, when present, must be a number >= 0.
    if (schedule.perUnitFee !== undefined && schedule.perUnitFee !== null) {
      if (typeof schedule.perUnitFee !== 'number' || isNaN(schedule.perUnitFee) || schedule.perUnitFee < 0) {
        errors.push({
          field: `${prefix}.perUnitFee`,
          message: `${prefix}.perUnitFee must be a non-negative number`,
        });
      }
    }

    // exerciseFeePerContract, when present, must be a number >= 0. Additive
    // exercise/assignment fee; does not count toward the "at least one fee
    // component" requirement below (a schedule may charge only on exercise).
    if (schedule.exerciseFeePerContract !== undefined && schedule.exerciseFeePerContract !== null) {
      if (
        typeof schedule.exerciseFeePerContract !== 'number' ||
        isNaN(schedule.exerciseFeePerContract) ||
        schedule.exerciseFeePerContract < 0
      ) {
        errors.push({
          field: `${prefix}.exerciseFeePerContract`,
          message: `${prefix}.exerciseFeePerContract must be a non-negative number`,
        });
      }
    }

    // At least one populated fee component (> 0).
    const flatPopulated = typeof schedule.flatFee === 'number' && schedule.flatFee > 0;
    const percentagePopulated = typeof schedule.percentageRate === 'number' && schedule.percentageRate > 0;
    const perUnitPopulated =
      schedule.perUnitFee !== undefined &&
      schedule.perUnitFee !== null &&
      typeof schedule.perUnitFee === 'number' &&
      schedule.perUnitFee > 0;
    if (!flatPopulated && !percentagePopulated && !perUnitPopulated) {
      errors.push({
        field: prefix,
        message: `${prefix} must define at least one fee component`,
      });
    }

    // gstRate must be a number in [0, 100].
    if (
      typeof schedule.gstRate !== 'number' ||
      isNaN(schedule.gstRate) ||
      schedule.gstRate < 0 ||
      schedule.gstRate > 100
    ) {
      errors.push({ field: `${prefix}.gstRate`, message: `${prefix}.gstRate must be between 0 and 100` });
    }

    // gstEnabled must be a boolean (defensive; TS enforces the type statically).
    if (typeof schedule.gstEnabled !== 'boolean') {
      errors.push({ field: `${prefix}.gstEnabled`, message: `${prefix}.gstEnabled must be a boolean` });
    }

    // currency must be a non-empty string.
    if (typeof schedule.currency !== 'string' || schedule.currency.trim().length === 0) {
      errors.push({ field: `${prefix}.currency`, message: `${prefix}.currency is required` });
    }
  });

  return errors;
}

/**
 * Validates exit alert threshold.
 * Must be an integer between 1 and 50 (percentage).
 * Returns all validation errors (not fail-fast).
 */
export function validateExitAlertThreshold(threshold: number): ValidationError[] {
  const errors: ValidationError[] = [];

  if (
    typeof threshold !== 'number' ||
    isNaN(threshold) ||
    !Number.isInteger(threshold) ||
    threshold < THRESHOLD_MIN ||
    threshold > THRESHOLD_MAX
  ) {
    errors.push({
      field: 'exitAlertThreshold',
      message: `exitAlertThreshold must be an integer between ${THRESHOLD_MIN} and ${THRESHOLD_MAX}`,
    });
  }

  return errors;
}

/**
 * Validates a theme preference.
 * Must be exactly one of the strings 'Light', 'Dark', or 'System'.
 * Returns all validation errors (not fail-fast); the value type is `unknown`
 * because it may arrive unvalidated off the wire.
 */
export function validateThemePreference(value: unknown): ValidationError[] {
  const errors: ValidationError[] = [];

  if (value !== 'Light' && value !== 'Dark' && value !== 'System') {
    errors.push({
      field: 'themePreference',
      message: "themePreference must be one of 'Light', 'Dark', or 'System'",
    });
  }

  return errors;
}

/**
 * Validates a password against the password policy.
 * Policy: min 8 chars, at least 1 uppercase, 1 lowercase, 1 digit, 1 special character.
 * Returns all validation errors (not fail-fast).
 */
export function validatePassword(password: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (typeof password !== 'string' || password.length < 8) {
    errors.push({ field: 'password', message: 'Password must be at least 8 characters long' });
  }

  if (typeof password !== 'string' || !/[A-Z]/.test(password)) {
    errors.push({ field: 'password', message: 'Password must contain at least one uppercase letter' });
  }

  if (typeof password !== 'string' || !/[a-z]/.test(password)) {
    errors.push({ field: 'password', message: 'Password must contain at least one lowercase letter' });
  }

  if (typeof password !== 'string' || !/\d/.test(password)) {
    errors.push({ field: 'password', message: 'Password must contain at least one digit' });
  }

  if (typeof password !== 'string' || !/[^A-Za-z0-9]/.test(password)) {
    errors.push({ field: 'password', message: 'Password must contain at least one special character' });
  }

  return errors;
}

// ─── Protective Put Validators ───────────────────────────────────────────────

/**
 * Validates a combined protective-put entry input (buy stock + buy a long put).
 * Returns all validation errors (not fail-fast).
 *
 * Exchange membership is checked in the service (matching how buy/write
 * validation defers the exchange check), since the shared validators do not
 * cover exchange.
 */
export function validateProtectivePutEntryInput(input: ProtectivePutEntryInput): ValidationError[] {
  const errors: ValidationError[] = [];

  validateTicker(input.ticker, 'ticker', errors);

  if (validateRequired(input.tradeDate, 'tradeDate', errors)) {
    validateISODate(input.tradeDate, 'tradeDate', errors);
  }

  // purchasePrice > 0 (PRICE_MIN is 0.01, so this enforces the positive-price rule).
  validatePrice(input.purchasePrice, 'purchasePrice', errors);

  // quantity > 0 (integer within range) AND an exact multiple of 100.
  validateQuantity(input.quantity, 'quantity', errors);
  if (
    typeof input.quantity === 'number' &&
    !isNaN(input.quantity) &&
    Number.isInteger(input.quantity) &&
    input.quantity % OPTION_CONTRACT_MULTIPLIER !== 0
  ) {
    errors.push({
      field: 'quantity',
      message: `quantity must be a multiple of ${OPTION_CONTRACT_MULTIPLIER}`,
    });
  }

  // putStrikePrice > 0.
  validatePrice(input.putStrikePrice, 'putStrikePrice', errors);

  // putPremium >= 0 (PREMIUM_MIN is 0.00).
  validatePremium(input.putPremium, 'putPremium', errors);

  // putExpiryDate required + valid ISO + on/after tradeDate.
  if (validateRequired(input.putExpiryDate, 'putExpiryDate', errors)) {
    if (validateISODate(input.putExpiryDate, 'putExpiryDate', errors)) {
      if (input.tradeDate && !isNaN(new Date(input.tradeDate).getTime())) {
        const expiry = new Date(input.putExpiryDate);
        const trade = new Date(input.tradeDate);
        if (expiry < trade) {
          errors.push({
            field: 'putExpiryDate',
            message: 'putExpiryDate must be on or after tradeDate',
          });
        }
      }
    }
  }

  // putContracts must equal quantity / 100.
  if (
    typeof input.quantity === 'number' &&
    !isNaN(input.quantity) &&
    Number.isInteger(input.quantity) &&
    input.quantity % OPTION_CONTRACT_MULTIPLIER === 0
  ) {
    const requiredContracts = input.quantity / OPTION_CONTRACT_MULTIPLIER;
    if (input.putContracts !== requiredContracts) {
      errors.push({
        field: 'putContracts',
        message: `putContracts must equal ${requiredContracts} (quantity / ${OPTION_CONTRACT_MULTIPLIER})`,
      });
    }
  }

  // Optional commissions must be >= 0 when present.
  if (input.stockCommission !== undefined) {
    validateCommission(input.stockCommission, 'stockCommission', errors);
  }
  if (input.putCommission !== undefined) {
    validateCommission(input.putCommission, 'putCommission', errors);
  }

  return errors;
}

/**
 * Validates a put-only input for attaching a protective put to an existing
 * open stock holding. Returns all validation errors (not fail-fast).
 *
 * The `putContracts === quantity / 100`, position-open, and
 * "expiry on/after trade date" cross-checks that need the fetched position are
 * performed in the service, not here.
 */
export function validateAddProtectivePutInput(input: AddProtectivePutInput): ValidationError[] {
  const errors: ValidationError[] = [];

  // putStrikePrice > 0.
  validatePrice(input.putStrikePrice, 'putStrikePrice', errors);

  // putPremium >= 0.
  validatePremium(input.putPremium, 'putPremium', errors);

  // putExpiryDate required + valid ISO.
  if (validateRequired(input.putExpiryDate, 'putExpiryDate', errors)) {
    validateISODate(input.putExpiryDate, 'putExpiryDate', errors);
  }

  // putContracts present (> 0).
  validateQuantity(input.putContracts, 'putContracts', errors);

  // putCommission >= 0 when present.
  if (input.putCommission !== undefined) {
    validateCommission(input.putCommission, 'putCommission', errors);
  }

  return errors;
}

/**
 * Validates a protective-put outcome input.
 * Returns all validation errors (not fail-fast).
 */
export function validatePutOutcomeInput(input: PutOutcomeInput): ValidationError[] {
  const errors: ValidationError[] = [];

  // outcome must be a member of the PutOutcome enum.
  if (!Object.values(PutOutcome).includes(input.outcome)) {
    errors.push({ field: 'outcome', message: 'outcome must be a valid put outcome' });
  }

  // outcomeDate required + valid ISO.
  if (validateRequired(input.outcomeDate, 'outcomeDate', errors)) {
    validateISODate(input.outcomeDate, 'outcomeDate', errors);
  }

  // When selling to close, proceeds and close commission must both be present and >= 0.
  if (input.outcome === PutOutcome.CLOSED) {
    if (validateRequired(input.saleProceedsPerShare, 'saleProceedsPerShare', errors)) {
      validatePremium(input.saleProceedsPerShare as number, 'saleProceedsPerShare', errors);
    }
    if (validateRequired(input.closeCommission, 'closeCommission', errors)) {
      validateCommission(input.closeCommission as number, 'closeCommission', errors);
    }
  }

  // On exercise the shares are sold (position closes). Both the exercise sale
  // price and the stock-sale commission are OPTIONAL (price defaults to the put
  // strike; commission can be auto-resolved), but when supplied they must be valid.
  if (input.outcome === PutOutcome.EXERCISED) {
    if (input.exerciseSalePrice !== undefined) {
      validatePrice(input.exerciseSalePrice, 'exerciseSalePrice', errors);
    }
    if (input.closeCommission !== undefined) {
      validateCommission(input.closeCommission, 'closeCommission', errors);
    }
  }

  return errors;
}

/**
 * Validates a covered-call option-outcome input.
 * Returns all validation errors (not fail-fast).
 *
 * Only the two outcomes the dialog offers are accepted here: EXPIRED
 * (worthless) and ASSIGNED (called away). CLOSED and ROLLED are produced by
 * the existing close/roll flows, not this entry point, so they are rejected.
 */
export function validateOptionOutcomeInput(input: OptionOutcomeInput): ValidationError[] {
  const errors: ValidationError[] = [];

  // outcome must be a member of OptionOutcome AND one of EXPIRED / ASSIGNED.
  if (
    !Object.values(OptionOutcome).includes(input.outcome) ||
    (input.outcome !== OptionOutcome.EXPIRED && input.outcome !== OptionOutcome.ASSIGNED)
  ) {
    errors.push({ field: 'outcome', message: 'outcome must be EXPIRED or ASSIGNED' });
  }

  // outcomeDate required + valid ISO.
  if (validateRequired(input.outcomeDate, 'outcomeDate', errors)) {
    validateISODate(input.outcomeDate, 'outcomeDate', errors);
  }

  // saleCommission, when supplied, must be a valid commission (ASSIGNED only;
  // optional because it is auto-resolved from the broker profile).
  if (input.saleCommission !== undefined) {
    validateCommission(input.saleCommission, 'saleCommission', errors);
  }

  return errors;
}

// ─── Dividend Validators ─────────────────────────────────────────────────────

/**
 * Validates a dividend record input.
 * Returns all validation errors (not fail-fast).
 *
 * - perShare: number, not NaN, > 0, <= 999,999.99, <= 2 decimal places.
 * - paymentDate: required + valid ISO 8601 date.
 */
export function validateDividendInput(input: DividendInput): ValidationError[] {
  const errors: ValidationError[] = [];

  validatePerShare(input.perShare, 'perShare', errors);

  if (validateRequired(input.paymentDate, 'paymentDate', errors)) {
    validateISODate(input.paymentDate, 'paymentDate', errors);
  }

  return errors;
}

/**
 * Validates a dividend update input.
 * Returns all validation errors (not fail-fast).
 *
 * Same perShare + paymentDate checks as validateDividendInput, plus an
 * expectedVersion that must be a number (the actual conflict check is in the
 * service against the stored version).
 */
export function validateDividendUpdateInput(input: DividendUpdateInput): ValidationError[] {
  const errors: ValidationError[] = [];

  validatePerShare(input.perShare, 'perShare', errors);

  if (validateRequired(input.paymentDate, 'paymentDate', errors)) {
    validateISODate(input.paymentDate, 'paymentDate', errors);
  }

  if (typeof input.expectedVersion !== 'number' || isNaN(input.expectedVersion)) {
    errors.push({ field: 'expectedVersion', message: 'expectedVersion must be a number' });
  }

  return errors;
}
